import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import { queryBuilder } from '@wix/sdk-runtime/query-builder';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import {
  HttpClient,
  HttpResponse,
  NonNullablePaths,
  QuerySpec,
  Query as QuerySdkType,
} from '@wix/sdk-types';
import * as ambassadorWixSocialGroupsV2Group from './social-groups-v2-group-groups.http.js';
// @ts-ignore
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { createQueryUtils } from '@wix/sdk-runtime/query-builder-utils';

/**
 * A group object represents a community space where site members can connect and share content.
 * You can create groups and manage their visibility, settings, and metadata.
 * Learn more about [groups](https://support.wix.com/en/article/wix-groups-about-groups).
 */
export interface Group {
  /**
   * Group ID.
   * @readonly
   * @format GUID
   */
  _id?: string | null;
  /**
   * A unique part of a group's URL, for example `https:/example.com/groups/slug`.
   * @minLength 1
   * @maxLength 100
   */
  slug?: string | null;
  /** Group privacy status. */
  privacyStatus?: PrivacyStatusWithLiterals;
  /**
   * Group name.
   * @maxLength 100
   */
  name?: string | null;
  /**
   * Group description in [DraftJS](https://draftjs.org) format.
   * @maxLength 20480
   */
  description?: string | null;
  /**
   * Group teaser.
   * @maxLength 1000
   */
  teaser?: string | null;
  /**
   * What group members are called, for example `Coworkers`, `Friends`, or `Students`.
   * @minLength 1
   * @maxLength 1000
   */
  memberTitle?: string | null;
  /** Cover image. You cannot upload your own cover image. */
  coverImage?: CoverImage;
  /**
   * Group specific settings.
   *
   * These settings can also be found in [a site's Dashboard](https://www.wix.com/my-account/site-selector/?buttonText=Select%20Site&title=Select%20a%20Site&autoSelectOnSingleSite=true&actionUrl=https:%2F%2Fwww.wix.com%2Fdashboard%2F%7B%7BmetaSiteId%7D%7D%2Fwix-groups/settings?).
   */
  settings?: GroupSettings;
  /**
   * Total count of current group members.
   * @readonly
   */
  membersCount?: number | null;
  /**
   * Group owner.
   * @readonly
   * @format GUID
   */
  ownerId?: string | null;
  /**
   * Group creation date and time.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time of the latest group update.
   * @readonly
   */
  _updatedDate?: Date | null;
  /**
   * Date and time of the most recent group activity, for example a post or comment.
   * @readonly
   */
  lastActivityDate?: Date | null;
}

export enum Type {
  UNKNOWN = 'UNKNOWN',
  ADMIN_APPROVAL = 'ADMIN_APPROVAL',
  PAID_PLANS = 'PAID_PLANS',
  EVENTS = 'EVENTS',
}

/** @enumType */
export type TypeWithLiterals =
  | Type
  | 'UNKNOWN'
  | 'ADMIN_APPROVAL'
  | 'PAID_PLANS'
  | 'EVENTS';

export interface Events {
  /** @format GUID */
  eventIds?: string[];
}

export interface Logo {
  /**
   * Logo image ID (for internal use).
   * @maxLength 1000
   */
  mediaId?: string | null;
  /** Logo image width. */
  width?: number | null;
  /** Logo image height. */
  height?: number | null;
  /**
   * Alternative text.
   * @maxLength 200
   */
  altText?: string | null;
}

export interface GroupDetailsPosition {
  /** horizontal coordinate */
  x?: number;
  /** vertical coordinate */
  y?: number;
}

export interface Image {
  /**
   * Image ID (for internal use).
   * @maxLength 1000
   */
  mediaId?: string | null;
  /** Image width. */
  width?: number | null;
  /** Image height. */
  height?: number | null;
  /** Indicates pre-configured/auto-generated images (from templates, client generated). */
  preset?: boolean | null;
}

export interface Position {
  /** horizontal coordinate */
  x?: number;
  /** vertical coordinate */
  y?: number;
}

export enum AllowPolicy {
  UNKNOWN = 'UNKNOWN',
  OWNER_AND_ADMINS = 'OWNER_AND_ADMINS',
  OWNER = 'OWNER',
  ALL_MEMBERS = 'ALL_MEMBERS',
}

/** @enumType */
export type AllowPolicyWithLiterals =
  | AllowPolicy
  | 'UNKNOWN'
  | 'OWNER_AND_ADMINS'
  | 'OWNER'
  | 'ALL_MEMBERS';

export interface OnboardingStepSettings {
  stepKey?: StepKeyWithLiterals;
  visible?: boolean;
}

export enum StepKey {
  UNKNOWN = 'UNKNOWN',
  CREATE_POST = 'CREATE_POST',
  REACT_TO_POST = 'REACT_TO_POST',
  INVITE_MEMBERS = 'INVITE_MEMBERS',
}

/** @enumType */
export type StepKeyWithLiterals =
  | StepKey
  | 'UNKNOWN'
  | 'CREATE_POST'
  | 'REACT_TO_POST'
  | 'INVITE_MEMBERS';

export enum PrivacyStatus {
  /** Undefined group privacy status. */
  UNKNOWN = 'UNKNOWN',
  /** Anyone can see the group and its content. Anyone can join the group. */
  PUBLIC = 'PUBLIC',
  /** Anyone can see the group, but only members can see its content. New members must submit a `Join Group Request`. */
  PRIVATE = 'PRIVATE',
  /** Only admins and members can see the group. New members can only be added by other members. */
  SECRET = 'SECRET',
}

/** @enumType */
export type PrivacyStatusWithLiterals =
  | PrivacyStatus
  | 'UNKNOWN'
  | 'PUBLIC'
  | 'PRIVATE'
  | 'SECRET';

export interface AccessRestriction extends AccessRestrictionDataOneOf {
  events?: Events;
  type?: TypeWithLiterals;
}

/** @oneof */
export interface AccessRestrictionDataOneOf {
  events?: Events;
}

export interface GroupDetails {
  /** Group logo. You cannot upload your own logo. */
  logo?: Logo;
  /**
   * What group members are called, for example `Coworkers`, `Friends`, or `Students`.
   * @minLength 1
   * @maxLength 1000
   */
  membersTitle?: string | null;
}

/** Cover image. You cannot upload your own cover image. */
export interface CoverImage {
  /** Cover image. */
  image?: Image;
  /** Position of the corner of the cover image (or logo). */
  position?: Position;
  /** Position of the corner of the cover image (or logo) for mobile browser. */
  mobilePosition?: Position;
  /**
   * Alternative text is typically a relatively short phrase that describes what the image depicts.
   *
   * The alternative text is used:
   * + If the browser cannot display the image.
   * + If the user is utilizing a screen reader.
   * + By search engines to understand what images are on your site.
   * @maxLength 200
   */
  altText?: string | null;
}

export interface GroupSettings {
  /**
   * __Deprecated.__ Use `allowedToInviteMembers` instead.
   * Whether regular members are permitted to invite new members.
   * If `false`, only admins can invite members. Defaults to `true`.
   * @deprecated
   */
  membersCanInvite?: boolean | null;
  /**
   * __Deprecated.__ Use `allowedToApproveJoinRequests` instead.
   * Whether all group members are permitted to approve join group requests.
   * If `false`, member approval is limited to the admins.
   * @deprecated
   */
  membersCanApprove?: boolean | null;
  /** Whether a daily post about new members is enabled. */
  welcomeMemberPostEnabled?: boolean | null;
  /** Whether an automatic post about changing the group details is enabled. */
  groupDetailsChangedPostEnabled?: boolean | null;
  /** Whether all members can see the full member list. */
  showMemberList?: boolean | null;
  /** Determines who can invite members to the group */
  allowedToInviteMembers?: AllowPolicyWithLiterals;
  /** Determines who can approve member join requests to the group */
  allowedToApproveJoinRequests?: AllowPolicyWithLiterals;
  /**
   * Whether AI spam protection is enabled for post creation in the group.
   * If not explicitly set, the default behavior is 'true' (enabled).
   */
  aiSpamProtectionEnabled?: boolean | null;
}

export interface Identity {
  /**
   * Member ID of the group creator.  See the Members API for more details.
   * @format GUID
   */
  _id?: string | null;
  identityType?: IdentityTypeWithLiterals;
}

export enum IdentityType {
  /** Wix user. */
  USER = 'USER',
  /** Wix member. */
  MEMBER = 'MEMBER',
}

/** @enumType */
export type IdentityTypeWithLiterals = IdentityType | 'USER' | 'MEMBER';

export interface CreateGroupRequest {
  /** Group to create. */
  group: Group;
  /**
   * ID of the member who created the group, from the Members API. This member will automatically become an admin.
   * @format GUID
   */
  creatorId?: string | null;
  /** Content type. */
  contentType?: ContentTypeWithLiterals;
}

export enum ContentType {
  PLAIN_TEXT = 'PLAIN_TEXT',
  DRAFTJS = 'DRAFTJS',
  RICH_CONTENT = 'RICH_CONTENT',
}

/** @enumType */
export type ContentTypeWithLiterals =
  | ContentType
  | 'PLAIN_TEXT'
  | 'DRAFTJS'
  | 'RICH_CONTENT';

export interface CreateGroupResponse {
  /** Created group. */
  group?: Group;
}

export interface UpdateGroupRequest {
  /** Group to update. */
  group: Group;
  /** Content type. */
  contentType?: ContentTypeWithLiterals;
}

export interface UpdateGroupResponse {
  /** Updated group. */
  group?: Group;
}

export interface GroupCoverChanged {
  /** Old URL of group cover. */
  oldUrl?: string | null;
  /** New URL of group cover. */
  newUrl?: string | null;
}

export interface GroupDescriptionChanged {
  /** Group's old description. */
  oldDescription?: string | null;
  /** Group's new description. */
  newDescription?: string | null;
}

export interface DeleteGroupRequest {
  /**
   * ID of the group to delete.
   * @format GUID
   */
  groupId: string;
}

export interface DeleteGroupResponse {
  /** Deleted group. */
  group?: Group;
}

export interface GetGroupRequest {
  /**
   * ID of the group to retrieve.
   * @format GUID
   */
  groupId: string;
  /** Content type. */
  contentType?: ContentTypeWithLiterals;
}

export interface GetGroupResponse {
  /** Retrieved group. */
  group?: Group;
}

export interface GetGroupBySlugRequest {
  /**
   * Unique part of the group's URL. For example, if a group's URL is `https:/example.com/groups/{my-fitness-group}`, the slug is `my-fitness-group`. Case-sensitive.
   * @minLength 1
   * @maxLength 100
   */
  slug: string;
  /** @format GUID */
  autoInviteId?: string | null;
  /** Content type. */
  contentType?: ContentTypeWithLiterals;
}

export interface GetGroupBySlugResponse {
  /** Retrieved group. */
  group?: Group;
}

export interface GetGroupIdBySlugRequest {
  /**
   * Unique part of the group's URL, for example `group-1` in `https:/example.com/groups/group-1`. Pass only the slug. Case-sensitive.
   * @minLength 1
   * @maxLength 100
   */
  slug?: string;
  /** @format GUID */
  autoInviteId?: string | null;
}

export interface GetGroupIdBySlugResponse {
  /**
   * ID of the group that correspond to slug
   * @format GUID
   */
  groupId?: string;
  /** Global feed permissions */
  accessPermissions?: GlobalFeedPermissions;
}

export interface GlobalFeedPermissions {
  feedPermissions?: FeedPermissions;
  ownFeedItemPermissions?: FeedItemPermissions;
  allFeedItemPermissions?: FeedItemPermissions;
  activityFeedItemPermissions?: FeedItemPermissions;
}

export interface FeedPermissions {
  canViewPosts?: boolean;
  canCreatePosts?: boolean;
  canPinPosts?: boolean;
  canCreateTopic?: boolean;
  canFollowPosts?: boolean;
  canCreatePaidPosts?: boolean;
  canReadSettings?: boolean;
  canManageSettings?: boolean;
}

export interface FeedItemPermissions {
  canViewFullPost?: boolean;
  canAssignTopic?: boolean;
  canUnassignTopic?: boolean;
  /** bool canFollow = 3; // it's not related to context token */
  canUpdate?: boolean;
  canDelete?: boolean;
  /** always true */
  canShare?: boolean;
  canAddComment?: boolean;
  canReact?: boolean;
  canPin?: boolean;
}

export interface ListGroupsRequest {
  /**
   * Number of items to load. Maximum `100`.
   * @max 100
   */
  limit?: number | null;
  /** Number of items to skip in the current sort order. */
  offset?: number | null;
  /** Content type. */
  contentType?: ContentTypeWithLiterals;
}

export interface ListGroupsResponse {
  /** Retrieved Groups. */
  groups?: Group[];
  /** Paging metadata. */
  metadata?: PagingMetadata;
}

export interface PagingMetadata {
  /** Number of items returned in the response. */
  count?: number | null;
  /** Offset that was requested. */
  offset?: number | null;
  /** Total number of items that match the query. */
  total?: number | null;
  /** Flag that indicates the server failed to calculate the `total` field. */
  tooManyToCount?: boolean | null;
}

export interface ListGroupsByUserIdRequest {
  /** Content type. */
  contentType?: ContentTypeWithLiterals;
}

export interface ListGroupsByUserIdResponse {
  groups?: GroupWithMsId[];
}

/** Retrieved Groups by metasite id */
export interface GroupWithMsId {
  metaSiteId?: string;
  groups?: Group[];
}

export interface QueryGroupsRequest {
  /** Query options. */
  query?: Query;
  /** Content type. */
  contentType?: ContentTypeWithLiterals;
}

export interface Query {
  /**
   * Filter object in the following format:
   * `"filter" : {
   * "fieldName1": "value1",
   * "fieldName2":{"$operator":"value2"}
   * }`
   * Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`
   */
  filter?: any;
  /**
   * Sort object in the following format:
   * `[{"fieldName":"sortField1","order":"ASC"},{"fieldName":"sortField2","order":"DESC"}]`
   */
  sort?: Sorting[];
  /** Paging options to limit and skip the number of items. */
  paging?: Paging;
  /** Array of projected fields. A list of specific field names to return. If `fieldsets` are also specified, the union of `fieldsets` and `fields` is returned. */
  fields?: string[];
  /** Array of named, predefined sets of projected fields. A array of predefined named sets of fields to be returned. Specifying multiple `fieldsets` will return the union of fields from all sets. If `fields` are also specified, the union of `fieldsets` and `fields` is returned. */
  fieldsets?: string[];
}

export interface Sorting {
  /**
   * Name of the field to sort by.
   * @maxLength 512
   */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface Paging {
  /** Number of items to load. */
  limit?: number | null;
  /** Number of items to skip in the current sort order. */
  offset?: number | null;
}

export interface QueryGroupsResponse {
  /** Retrieved groups. */
  groups?: Group[];
  /** Paging metadata. */
  metadata?: PagingMetadata;
}

export interface QueryJoinedGroupsRequest {
  /** Query options. */
  query?: Query;
  /** Content type. */
  contentType?: ContentTypeWithLiterals;
}

export interface QueryJoinedGroupsResponse {
  /** Retrieved groups. */
  groups?: Group[];
  /** Paging metadata. */
  metadata?: PagingMetadata;
}

export interface QueryGroupsByMembershipRequest {
  /** Query options. */
  query?: Query;
  membershipStatus?: MembershipStatusWithLiterals;
  /** Filter groups by permissions. */
  permissionsFilter?: GroupPermissions;
  /** Content type. */
  contentType?: ContentTypeWithLiterals;
}

export enum MembershipStatus {
  NONE = 'NONE',
  JOINED = 'JOINED',
  PENDING = 'PENDING',
}

/** @enumType */
export type MembershipStatusWithLiterals =
  | MembershipStatus
  | 'NONE'
  | 'JOINED'
  | 'PENDING';

export interface GroupPermissions {
  canCreatePosts?: boolean | null;
}

export interface QueryGroupsByMembershipResponse {
  /** Retrieved groups. */
  groups?: Group[];
  /** Paging metadata. */
  metadata?: PagingMetadata;
}

export interface ListGroupIntegrationsDataRequest {
  /**
   * @format GUID
   * @minSize 1
   * @maxSize 100
   */
  groupIds?: string[];
}

export interface ListGroupIntegrationsDataResponse {
  /**
   * @minSize 1
   * @maxSize 100
   */
  groupsIntegrationsData?: GroupIntegrationsData[];
}

export interface GroupIntegrationsData {
  /** @format GUID */
  groupId?: string;
  feedItemsCount?: number;
  /** @format GUID */
  topicIds?: string[];
  /** @format GUID */
  eventIds?: string[];
  /** @format GUID */
  connectedPricingPlanIds?: string[];
  /** @format GUID */
  onlineProgramIds?: string[];
}

export interface GetGroupMembersGroupIdsRequest {
  /**
   * ID of the group to retrieve members group ids.
   * @format GUID
   */
  groupId?: string;
}

export interface GetGroupMembersGroupIdsResponse {
  /**
   * All members group id
   * @format GUID
   */
  allMembersGroupId?: string;
  /**
   * Admins members group id
   * @format GUID
   */
  adminMembersGroupId?: string;
}

export interface GetGroupBMFeaturesRequest {
  /**
   * ID of the group to retrieve BM features.
   * @format GUID
   */
  groupId?: string;
}

export interface GetGroupBMFeaturesResponse {
  level?: number;
  features?: BMFeatures;
}

export interface BMFeatures {
  /** @maxSize 10 */
  bmFeatures?: BMFeatureWithLiterals[];
}

export enum BMFeature {
  BM_FEATURES_UNKNOWN = 'BM_FEATURES_UNKNOWN',
  BM_FEATURES_GENERATE_TOPICS = 'BM_FEATURES_GENERATE_TOPICS',
  BM_FEATURES_GENERATE_QUESTIONS = 'BM_FEATURES_GENERATE_QUESTIONS',
}

/** @enumType */
export type BMFeatureWithLiterals =
  | BMFeature
  | 'BM_FEATURES_UNKNOWN'
  | 'BM_FEATURES_GENERATE_TOPICS'
  | 'BM_FEATURES_GENERATE_QUESTIONS';

export interface QueryJoinedGroupsWithMemberRequest {
  /** Query options. */
  query?: Query;
  /**
   * ID of the member to find common groups with
   * @format GUID
   */
  memberId?: string;
  /** Content type. */
  contentType?: ContentTypeWithLiterals;
}

export interface QueryJoinedGroupsWithMemberResponse {
  /** Retrieved groups. */
  groups?: Group[];
  /** Paging metadata. */
  metadata?: PagingMetadata;
}

export interface GetPrivacyRequest {
  /**
   * Group ids to list privacy for
   * @format GUID
   * @maxSize 100
   */
  groupIds?: string[];
}

export interface GetPrivacyResponse {
  /** Privacy statuses listed per group id */
  privacyStatuses?: Record<string, PrivacyStatus>;
}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entity?: string;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntity?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntity?: string | null;
}

export interface ActionEvent {
  body?: string;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

export interface BaseEventMetadata {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface EventMetadata extends BaseEventMetadata {
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
  accountInfo?: AccountInfoMetadata;
}

export interface AccountInfoMetadata {
  /** ID of the Wix account associated with the event */
  accountId: string;
  /** ID of the Wix site associated with the event. Only included when the event is tied to a specific site. */
  siteId?: string;
  /** ID of the parent Wix account. Only included when 'accountId' belongs to a child account. */
  parentAccountId?: string;
}

export interface GroupCreatedEnvelope {
  entity: Group;
  metadata: EventMetadata;
}

/**
 * Triggered when a group is created.
 * @webhook
 * @eventType wix.social_groups.v2.group_created
 * @serviceIdentifier wix.social.groups.api.v2.GroupsService
 * @slug created
 */
export declare function onGroupCreated(
  handler: (event: GroupCreatedEnvelope) => void | Promise<void>
): void;

export interface GroupDeletedEnvelope {
  metadata: EventMetadata;
}

/**
 * Triggered when a group is deleted.
 * @webhook
 * @eventType wix.social_groups.v2.group_deleted
 * @serviceIdentifier wix.social.groups.api.v2.GroupsService
 * @slug deleted
 */
export declare function onGroupDeleted(
  handler: (event: GroupDeletedEnvelope) => void | Promise<void>
): void;

export interface GroupCoverChangedEnvelope {
  data: GroupCoverChanged;
  metadata: EventMetadata;
}

/**
 * Triggered when a group's logo is changed.
 * @webhook
 * @eventType wix.social_groups.v2.group_group_cover_changed
 * @serviceIdentifier wix.social.groups.api.v2.GroupsService
 * @slug group_cover_changed
 */
export declare function onGroupCoverChanged(
  handler: (event: GroupCoverChangedEnvelope) => void | Promise<void>
): void;

export interface GroupDescriptionChangedEnvelope {
  data: GroupDescriptionChanged;
  metadata: EventMetadata;
}

/**
 * Triggered when a group's description is changed.
 * @webhook
 * @eventType wix.social_groups.v2.group_group_description_changed
 * @serviceIdentifier wix.social.groups.api.v2.GroupsService
 * @slug group_description_changed
 */
export declare function onGroupDescriptionChanged(
  handler: (event: GroupDescriptionChangedEnvelope) => void | Promise<void>
): void;

export interface GroupUpdatedEnvelope {
  entity: Group;
  metadata: EventMetadata;
}

/**
 * Triggered when a group is updated. See payload for more information about the specific trigger.
 * @webhook
 * @eventType wix.social_groups.v2.group_updated
 * @serviceIdentifier wix.social.groups.api.v2.GroupsService
 * @slug updated
 */
export declare function onGroupUpdated(
  handler: (event: GroupUpdatedEnvelope) => void | Promise<void>
): void;

/**
 * Creates a group.
 *
 * When a group is created, the newly created group is added to the [Groups List](https://support.wix.com/en/article/wix-groups-about-your-groups-pages#groups-group-list) page
 * of a site.
 *
 * Specify a `creatorId` to set the group's creator. The group's creator will automatically become a group admin. See [Terminology](https://dev.wix.com/docs/rest/crm/community/groups/terminology) for more information on roles.
 *
 *
 * Wix users determine who can create a group.
 * This setting can be found in [a site's Dashboard](https://www.wix.com/my-account/site-selector/?buttonText=Select%20Site&title=Select%20a%20Site&autoSelectOnSingleSite=true&actionUrl=https:%2F%2Fwww.wix.com%2Fdashboard%2F%7B%7BmetaSiteId%7D%7D%2Fwix-groups/settings?) under **Groups > General Settings > Group Creation**.
 * If set to **members with approval**, site members can create a group with the Create Group method, and the group becomes a `createRequest` with a status of `PENDING`.
 * A Wix user either approves or rejects the request to create a group.
 * If set to **all site members**, site members can create a group with the Create Group method and no approval is required.
 * If set to **only admins**, only Wix users can create a group with the Create Group method.
 * Default is set to **members with approval**.
 * @param group - Group to create.
 * @public
 * @requiredField group
 * @param options - Optional fields for group creation.
 * @returns Created group.
 * @fqn wix.social.groups.api.v2.GroupsService.CreateGroup
 */
export async function createGroup(
  group: Group,
  options?: CreateGroupOptions
): Promise<
  NonNullablePaths<
    Group,
    | `privacyStatus`
    | `coverImage.position.x`
    | `coverImage.position.y`
    | `settings.allowedToInviteMembers`
    | `settings.allowedToApproveJoinRequests`,
    4
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    group: group,
    creatorId: options?.creatorId,
    contentType: options?.contentType,
  });

  const reqOpts = ambassadorWixSocialGroupsV2Group.createGroup(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)?.group!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          group: '$[0]',
          creatorId: '$[1].creatorId',
          contentType: '$[1].contentType',
        },
        singleArgumentUnchanged: false,
      },
      ['group', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface CreateGroupOptions {
  /**
   * ID of the member who created the group, from the Members API. This member will automatically become an admin.
   * @format GUID
   */
  creatorId?: string | null;
  /** Content type. */
  contentType?: ContentTypeWithLiterals;
}

/**
 * Updates a group.
 *
 * When a public or private group's name is updated, the slug is updated to reflect the new group name.
 * Only group admins can update their group.
 *
 * > **Notes:**
 * > + When `group.privacyStatus` is updated from `PRIVATE` to `PUBLIC`, all pending group join requests are automatically approved.
 * > + When `group.privacyStatus` is updated from `PRIVATE` to `SECRET`, all pending group join requests are automatically rejected.
 * @param _id - Group ID.
 * @public
 * @requiredField _id
 * @requiredField group
 * @returns Updated group.
 * @fqn wix.social.groups.api.v2.GroupsService.UpdateGroup
 */
export async function updateGroup(
  _id: string,
  group: UpdateGroup,
  options?: UpdateGroupOptions
): Promise<
  NonNullablePaths<
    Group,
    | `privacyStatus`
    | `coverImage.position.x`
    | `coverImage.position.y`
    | `settings.allowedToInviteMembers`
    | `settings.allowedToApproveJoinRequests`,
    4
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[3] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    group: { ...group, id: _id },
    contentType: options?.contentType,
  });

  const reqOpts = ambassadorWixSocialGroupsV2Group.updateGroup(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)?.group!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: { group: '$[1]' },
        explicitPathsToArguments: {
          'group.id': '$[0]',
          contentType: '$[2].contentType',
        },
        singleArgumentUnchanged: false,
      },
      ['_id', 'group', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface UpdateGroup {
  /**
   * Group ID.
   * @readonly
   * @format GUID
   */
  _id?: string | null;
  /**
   * A unique part of a group's URL, for example `https:/example.com/groups/slug`.
   * @minLength 1
   * @maxLength 100
   */
  slug?: string | null;
  /** Group privacy status. */
  privacyStatus?: PrivacyStatusWithLiterals;
  /**
   * Group name.
   * @maxLength 100
   */
  name?: string | null;
  /**
   * Group description in [DraftJS](https://draftjs.org) format.
   * @maxLength 20480
   */
  description?: string | null;
  /**
   * Group teaser.
   * @maxLength 1000
   */
  teaser?: string | null;
  /**
   * What group members are called, for example `Coworkers`, `Friends`, or `Students`.
   * @minLength 1
   * @maxLength 1000
   */
  memberTitle?: string | null;
  /** Cover image. You cannot upload your own cover image. */
  coverImage?: CoverImage;
  /**
   * Group specific settings.
   *
   * These settings can also be found in [a site's Dashboard](https://www.wix.com/my-account/site-selector/?buttonText=Select%20Site&title=Select%20a%20Site&autoSelectOnSingleSite=true&actionUrl=https:%2F%2Fwww.wix.com%2Fdashboard%2F%7B%7BmetaSiteId%7D%7D%2Fwix-groups/settings?).
   */
  settings?: GroupSettings;
  /**
   * Total count of current group members.
   * @readonly
   */
  membersCount?: number | null;
  /**
   * Group owner.
   * @readonly
   * @format GUID
   */
  ownerId?: string | null;
  /**
   * Group creation date and time.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time of the latest group update.
   * @readonly
   */
  _updatedDate?: Date | null;
  /**
   * Date and time of the most recent group activity, for example a post or comment.
   * @readonly
   */
  lastActivityDate?: Date | null;
}

export interface UpdateGroupOptions {
  /** Content type. */
  contentType?: ContentTypeWithLiterals;
}

/** @param groupId - ID of the group to delete.
 * @public
 * @requiredField groupId
 * @fqn wix.social.groups.api.v2.GroupsService.DeleteGroup
 */
export async function deleteGroup(
  groupId: string
): Promise<
  NonNullablePaths<
    DeleteGroupResponse,
    | `group.privacyStatus`
    | `group.coverImage.position.x`
    | `group.coverImage.position.y`
    | `group.settings.allowedToInviteMembers`
    | `group.settings.allowedToApproveJoinRequests`,
    5
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ groupId: groupId });

  const reqOpts = ambassadorWixSocialGroupsV2Group.deleteGroup(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { groupId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['groupId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Retrieves a group.
 *
 * For groups with `group.privacyStatus` set to `SECRET`, only group admins and group members can see the group and its content.
 * @param groupId - ID of the group to retrieve.
 * @public
 * @requiredField groupId
 * @returns Retrieved group.
 * @fqn wix.social.groups.api.v2.GroupsService.GetGroup
 */
export async function getGroup(
  groupId: string,
  options?: GetGroupOptions
): Promise<
  NonNullablePaths<
    Group,
    | `privacyStatus`
    | `coverImage.position.x`
    | `coverImage.position.y`
    | `settings.allowedToInviteMembers`
    | `settings.allowedToApproveJoinRequests`,
    4
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    groupId: groupId,
    contentType: options?.contentType,
  });

  const reqOpts = ambassadorWixSocialGroupsV2Group.getGroup(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)?.group!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          groupId: '$[0]',
          contentType: '$[1].contentType',
        },
        singleArgumentUnchanged: false,
      },
      ['groupId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface GetGroupOptions {
  /** Content type. */
  contentType?: ContentTypeWithLiterals;
}

/**
 * Retrieves a group by slug.
 *
 * The slug is the end of a group's URL that refers to a specific group.
 * For example, if a group's URL is `https:/example.com/groups/{my-fitness-group}`, the slug is `my-fitness-group`.
 * Slugs are case-sensitive. It is generally based on the group name, but for secret groups it is an autogenerated string of characters,
 * for example, `https:/example.com/groups/{5D3yTX}`.
 *
 * For groups with `group.privacyStatus` set to `SECRET`, only group admins and group members can see the group and its content.
 * @param slug - Unique part of the group's URL. For example, if a group's URL is `https:/example.com/groups/{my-fitness-group}`, the slug is `my-fitness-group`. Case-sensitive.
 * @public
 * @requiredField slug
 * @fqn wix.social.groups.api.v2.GroupsService.GetGroupBySlug
 */
export async function getGroupBySlug(
  slug: string,
  options?: GetGroupBySlugOptions
): Promise<
  NonNullablePaths<
    GetGroupBySlugResponse,
    | `group.privacyStatus`
    | `group.coverImage.position.x`
    | `group.coverImage.position.y`
    | `group.settings.allowedToInviteMembers`
    | `group.settings.allowedToApproveJoinRequests`,
    5
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    slug: slug,
    autoInviteId: options?.autoInviteId,
    contentType: options?.contentType,
  });

  const reqOpts = ambassadorWixSocialGroupsV2Group.getGroupBySlug(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          slug: '$[0]',
          autoInviteId: '$[1].autoInviteId',
          contentType: '$[1].contentType',
        },
        singleArgumentUnchanged: false,
      },
      ['slug', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface GetGroupBySlugOptions {
  /** @format GUID */
  autoInviteId?: string | null;
  /** Content type. */
  contentType?: ContentTypeWithLiterals;
}

/**
 * Retrieves up to 100 groups.
 *
 *
 * Default sorts by `_createdDate` in descending order. For `SECRET` groups, only group admins and group members can see a list of groups and their content.
 *
 * > **Note:** This function's parameters are positional, and must be specified in the sequence shown in the syntax below. When specifying a parameter, use `null` as a placeholder for any unspecified parameters. For example, to specify limit only, call `listGroups(paging, null)`.
 * @public
 * @param options - Limit and offset options.
 * @fqn wix.social.groups.api.v2.GroupsService.ListGroups
 */
export async function listGroups(
  options?: ListGroupsOptions
): Promise<
  NonNullablePaths<
    ListGroupsResponse,
    | `groups`
    | `groups.${number}.privacyStatus`
    | `groups.${number}.coverImage.position.x`
    | `groups.${number}.coverImage.position.y`
    | `groups.${number}.settings.allowedToInviteMembers`
    | `groups.${number}.settings.allowedToApproveJoinRequests`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    limit: options?.limit,
    offset: options?.offset,
    contentType: options?.contentType,
  });

  const reqOpts = ambassadorWixSocialGroupsV2Group.listGroups(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          limit: '$[0].limit',
          offset: '$[0].offset',
          contentType: '$[0].contentType',
        },
        singleArgumentUnchanged: false,
      },
      ['options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface ListGroupsOptions {
  /**
   * Number of items to load. Maximum `100`.
   * @max 100
   */
  limit?: number | null;
  /** Number of items to skip in the current sort order. */
  offset?: number | null;
  /** Content type. */
  contentType?: ContentTypeWithLiterals;
}

/**
 * Retrieves up to 100 groups, given the provided paging, filtering, and sorting.
 *
 * Supported fields for filtering:
 * - `title`
 *
 * Supported fields for sorting:
 * - `title`
 * - `createdDate`
 * - `membersCount`
 * - `recentActivityDate`
 *
 * For groups with `group.privacyStatus` set to `SECRET`, only group admins and group members can see the group and its content.
 * @public
 * @fqn wix.social.groups.api.v2.GroupsService.QueryGroups
 */
export function queryGroups(options?: QueryGroupsOptions): GroupsQueryBuilder {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  return queryBuilder<Group, 'OFFSET', QueryGroupsRequest, QueryGroupsResponse>(
    {
      func: async (payload: QueryGroupsRequest) => {
        const reqOpts = ambassadorWixSocialGroupsV2Group.queryGroups({
          ...payload,
          ...(options ?? {}),
        });

        sideEffects?.onSiteCall?.();
        try {
          const result = await httpClient.request(reqOpts);
          sideEffects?.onSuccess?.(result);
          return result;
        } catch (err) {
          sideEffects?.onError?.(err);
          throw err;
        }
      },
      requestTransformer: (query: QueryGroupsRequest['query']) => {
        const args = [query, options] as [
          QueryGroupsRequest['query'],
          QueryGroupsOptions
        ];
        return renameKeysFromSDKRequestToRESTRequest({
          ...args?.[1],
          query: args?.[0],
        });
      },
      responseTransformer: ({ data }: HttpResponse<QueryGroupsResponse>) => {
        const transformedData = renameKeysFromRESTResponseToSDKResponse(
          transformPaths(data, [])
        );

        return {
          items: transformedData?.groups,
          pagingMetadata: transformedData?.metadata,
        };
      },
      errorTransformer: (err: unknown) => {
        const transformedError = sdkTransformError(err, {
          spreadPathsToArguments: {},
          explicitPathsToArguments: { query: '$[0]' },
          singleArgumentUnchanged: false,
        });

        throw transformedError;
      },
      pagingMethod: 'OFFSET',
      transformationPaths: {},
    }
  );
}

export interface QueryGroupsOptions {
  /** Content type. */
  contentType?: ContentTypeWithLiterals | undefined;
}

interface QueryOffsetResult {
  currentPage: number | undefined;
  totalPages: number | undefined;
  totalCount: number | undefined;
  hasNext: () => boolean;
  hasPrev: () => boolean;
  length: number;
  pageSize: number;
}

export interface GroupsQueryResult extends QueryOffsetResult {
  items: Group[];
  query: GroupsQueryBuilder;
  next: () => Promise<GroupsQueryResult>;
  prev: () => Promise<GroupsQueryResult>;
}

export interface GroupsQueryBuilder {
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  eq: (propertyName: string, value: any) => GroupsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  ne: (propertyName: string, value: any) => GroupsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  ge: (propertyName: string, value: any) => GroupsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  gt: (propertyName: string, value: any) => GroupsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  le: (propertyName: string, value: any) => GroupsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  lt: (propertyName: string, value: any) => GroupsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `string`.
   * @param string - String to compare against. Case-insensitive.
   */
  startsWith: (propertyName: string, value: string) => GroupsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `values`.
   * @param values - List of values to compare against.
   */
  hasSome: (propertyName: string, value: any[]) => GroupsQueryBuilder;
  in: (propertyName: string, value: any) => GroupsQueryBuilder;
  exists: (propertyName: string, value: boolean) => GroupsQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments. */
  ascending: (
    ...propertyNames: Array<'membersCount' | '_createdDate'>
  ) => GroupsQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments. */
  descending: (
    ...propertyNames: Array<'membersCount' | '_createdDate'>
  ) => GroupsQueryBuilder;
  /** @param limit - Number of items to return, which is also the `pageSize` of the results object. */
  limit: (limit: number) => GroupsQueryBuilder;
  /** @param skip - Number of items to skip in the query results before returning the results. */
  skip: (skip: number) => GroupsQueryBuilder;
  find: () => Promise<GroupsQueryResult>;
}

/**
 * @hidden
 * @fqn wix.social.groups.api.v2.GroupsService.QueryGroups
 * @requiredField query
 */
export async function typedQueryGroups(
  query: GroupQuery,
  options?: QueryGroupsOptions
): Promise<
  NonNullablePaths<
    QueryGroupsResponse,
    | `groups`
    | `groups.${number}.privacyStatus`
    | `groups.${number}.coverImage.position.x`
    | `groups.${number}.coverImage.position.y`
    | `groups.${number}.settings.allowedToInviteMembers`
    | `groups.${number}.settings.allowedToApproveJoinRequests`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    query: query,
    ...options,
  });

  const reqOpts = ambassadorWixSocialGroupsV2Group.queryGroups(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['query', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface GroupQuerySpec extends QuerySpec {
  paging: 'offset';
  wql: [];
}

export type CommonQueryWithEntityContext = QuerySdkType<Group, GroupQuerySpec>;
export type GroupQuery = {
  /** 
  Filter object in the following format:
  `"filter" : {
  "fieldName1": "value1",
  "fieldName2":{"$operator":"value2"}
  }`
  Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`  
  */
  filter?: CommonQueryWithEntityContext['filter'];
  /** 
  Sort object in the following format:
  `[{"fieldName":"sortField1","order":"ASC"},{"fieldName":"sortField2","order":"DESC"}]`  
  */
  sort?: {
    /** 
  Name of the field to sort by. 
  @maxLength: 512 
  */
    fieldName?: NonNullable<
      CommonQueryWithEntityContext['sort']
    >[number]['fieldName'];
    /** 
  Sort order.  
  */
    order?: NonNullable<CommonQueryWithEntityContext['sort']>[number]['order'];
  }[];
  /** 
  Paging options to limit and skip the number of items.  
  */
  paging?: {
    /** 
  Number of items to load.  
  */
    limit?: NonNullable<CommonQueryWithEntityContext['paging']>['limit'] | null;
    /** 
  Number of items to skip in the current sort order.  
  */
    offset?:
      | NonNullable<CommonQueryWithEntityContext['paging']>['offset']
      | null;
  };
};

export const utils = {
  ...createQueryUtils<Group, GroupQuerySpec, GroupQuery>(),
};
